import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Order, RegisterSession, CurrentUser, KitchenSLASettings } from '../types';
import KitchenOrderCard from '../components/kds/KitchenOrderCard';
import KitchenMealPlanBookingsPanel from '../components/kds/KitchenMealPlanBookingsPanel';
import { isSampleOrDemoOrderId } from '../lib/sampleOrderIds';
import RegisterClosedGuard from '../components/pos/RegisterClosedGuard';
import { resolveKitchenAlertSoundUrl } from '../lib/kitchenAlertSound';
import { getCardItemsWithTargets, getEffectiveQuantityCompletedForBatch, isKitchenRelevantCalendarDay, isOpenKitchenTicketStatus, isStaleOnlineKitchenTicket, mergeOrderPreferComplete, orderHasMeaningfulItems, pickLongerOrderSnapshot } from '../lib/orderUtils';
import type { LastSentItemEntry } from '../types';
import { orderCreatedAtMs, toMillis, ymdInTimeZone, ymdToEndMs, ymdToStartMs } from '../lib/orderTimestamps';
import { ordersService } from '../lib/database';

const BUSINESS_TZ = 'Pacific/Auckland';

interface KitchenDisplayPageProps {
  orders: Order[];
  registerSession: RegisterSession | undefined;
  currentUser?: CurrentUser | null;
  onUpdateOrderItemStatus: (orderId: string, itemIndex: number, status: 'pending' | 'completed', quantityCompleted?: number, batchIndex?: number, targetQtyForCard?: number) => void;
  onStartOrder?: (orderId: string, batchIndex?: number) => void;
  /** Soft-hide from kitchen only — does not delete the POS order. */
  onRemoveOrder?: (orderId: string) => Promise<void>;
  onOpenRegister?: () => void;
  slaSettings?: Partial<KitchenSLASettings>;
  /** False until App.tsx's `subscribeActive` listener has reported once. */
  registerSessionsHydrated?: boolean;
  /** Stacked phone board (waitstaff SPA) instead of desktop KDS grid. */
  phoneLayout?: boolean;
}

type KitchenTicketRow = { order: Order; batchIndex?: number; batchId?: string };
type BoardTicketRow = KitchenTicketRow & { listStage: 'new' | 'preparing' };

/** Online pickup tickets appear on the main board this many minutes before pickup. */
const ONLINE_KITCHEN_LEAD_MS = 30 * 60 * 1000;

function buildKitchenSnapshotFromOrder(order: Order): LastSentItemEntry[] {
  const items = order.items ?? [];
  return items
    .filter((item) => {
      if (item.removed) return false;
      const price = typeof item.price === 'number' ? item.price : 0;
      const id = String(item.id ?? '');
      return price >= 0 && !id.includes('combo-discount');
    })
    .map((item, idx) => ({
      lineId: item.lineId ?? `online-${idx}-${item.id}-${item.selectedMeatOption ?? ''}`,
      name: item.name,
      quantity: item.quantity,
      ...(item.notes?.trim() ? { notes: item.notes.trim() } : {}),
      ...(item.selectedMeatOption ? { selectedMeatOption: item.selectedMeatOption } : {}),
    }));
}

function normalizePickupTimeMs(raw: unknown): number | null {
  const ms = toMillis(raw);
  return ms > 0 ? ms : null;
}

function isOnlineScheduledPickup(order: Order, nowMs: number = Date.now()): boolean {
  if (order.orderType !== 'online' || order.paymentStatus !== 'paid') return false;
  const pickupMs = normalizePickupTimeMs(order.pickupTime);
  if (pickupMs == null) return false;
  return pickupMs > nowMs + ONLINE_KITCHEN_LEAD_MS;
}

/** Slim right-side page rail when more tickets fit than one full-height row. */
const KdsStagePager: React.FC<{
  page: number;
  totalPages: number;
  onChangePage: (p: number) => void;
  'aria-label': string;
  /** Wider rail for touch (e.g. awaiting service). */
  wideRail?: boolean;
}> = ({ page, totalPages, onChangePage, 'aria-label': ariaLabel, wideRail }) => {
  if (totalPages <= 1) return null;
  const railW = wideRail ? 'w-11 sm:w-12' : 'w-9 sm:w-10';
  return (
    <nav
      className={`kds-hide-scrollbar flex max-h-full ${railW} shrink-0 flex-col gap-1 overflow-y-auto border-l border-stone-800/60 bg-stone-900/95 py-1.5 pl-1.5 pr-1 shadow-inner`}
      aria-label={ariaLabel}
    >
      {Array.from({ length: totalPages }, (_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChangePage(i)}
          className={`flex min-h-8 w-full shrink-0 items-center justify-center rounded-sm text-[11px] font-black leading-none transition active:scale-[0.96] sm:min-h-9 sm:text-xs ${
            wideRail ? 'sm:min-h-10 sm:text-sm' : ''
          } ${
            i === page
              ? 'bg-amber-400 text-stone-900 shadow-sm ring-1 ring-stone-800/25'
              : 'bg-indigo-950/90 text-indigo-100 hover:bg-indigo-900'
          }`}
        >
          {i + 1}
        </button>
      ))}
    </nav>
  );
};

/** Prominent paging for awaiting-service view (easy to miss the slim right rail). */
const KdsAwaitingPageBar: React.FC<{
  page: number;
  totalPages: number;
  totalTickets: number;
  perPage: number;
  onChangePage: (p: number) => void;
}> = ({ page, totalPages, totalTickets, perPage, onChangePage }) => {
  if (totalPages <= 1) {
    return (
      <div className="flex shrink-0 justify-center border-b border-stone-400/50 bg-stone-800/85 px-3 py-2 text-center text-sm font-medium text-stone-200">
        {totalTickets} order{totalTickets !== 1 ? 's' : ''} on this screen
      </div>
    );
  }
  const from = page * perPage + 1;
  const to = Math.min(totalTickets, (page + 1) * perPage);
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-center gap-2 border-b border-stone-400/50 bg-stone-900 px-2 py-2 text-white sm:gap-3 sm:px-4">
      <button
        type="button"
        disabled={page <= 0}
        onClick={() => onChangePage(Math.max(0, page - 1))}
        className="min-h-10 rounded-md bg-stone-700 px-3 py-2 text-sm font-bold uppercase tracking-wide ring-1 ring-stone-600 hover:bg-stone-600 disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-11 sm:px-4"
      >
        Previous
      </button>
      <label className="flex min-w-0 max-w-full flex-wrap items-center justify-center gap-2 text-sm font-semibold text-stone-200">
        <span className="hidden shrink-0 sm:inline">Page</span>
        <select
          value={page}
          onChange={(e) => onChangePage(Number(e.target.value))}
          className="min-h-10 max-w-[min(100vw-11rem,240px)] cursor-pointer rounded-md border-2 border-amber-400/85 bg-stone-800 px-3 py-2 text-base font-extrabold text-white shadow-inner focus:outline-none focus:ring-2 focus:ring-amber-400"
          aria-label="Choose awaiting service page"
        >
          {Array.from({ length: totalPages }, (_, i) => (
            <option key={i} value={i}>
              Page {i + 1} of {totalPages}
            </option>
          ))}
        </select>
        <span className="shrink-0 tabular-nums text-stone-400">
          ({from}–{to} of {totalTickets})
        </span>
      </label>
      <button
        type="button"
        disabled={page >= totalPages - 1}
        onClick={() => onChangePage(Math.min(totalPages - 1, page + 1))}
        className="min-h-10 rounded-md bg-stone-700 px-3 py-2 text-sm font-bold uppercase tracking-wide ring-1 ring-stone-600 hover:bg-stone-600 disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-11 sm:px-4"
      >
        Next
      </button>
    </div>
  );
};

const KitchenDisplayPage: React.FC<KitchenDisplayPageProps> = ({ orders, registerSession, currentUser, onUpdateOrderItemStatus, onStartOrder, onRemoveOrder, onOpenRegister, slaSettings, registerSessionsHydrated = true, phoneLayout = false }) => {
  const [showAwaitingServiceView, setShowAwaitingServiceView] = useState(false);
  const [showScheduledView, setShowScheduledView] = useState(false);
  const [showMealPlanBookingsView, setShowMealPlanBookingsView] = useState(false);
  const [boardPage, setBoardPage] = useState(0);
  const [awaitingPage, setAwaitingPage] = useState(0);
  const [scheduledPage, setScheduledPage] = useState(0);
  /** Today's orders from Firestore (same fetch as POS Pending) — live listener can miss paid online tickets. */
  const [fetchedKitchenOrders, setFetchedKitchenOrders] = useState<Order[]>([]);
  const [newColumnHeaderPulse, setNewColumnHeaderPulse] = useState(false);
  const prevNewTicketCountRef = useRef<number>(0);
  const prevPendingStartRef = useRef<number | null>(null);
  const pendingStartSkipInitialRef = useRef(true);
  const alertAudioRef = useRef<HTMLAudioElement | null>(null);
  const slaSettingsRef = useRef(slaSettings);
  slaSettingsRef.current = slaSettings;

  const stopKitchenAlertSound = useCallback(() => {
    const a = alertAudioRef.current;
    if (a) {
      try {
        a.pause();
        a.currentTime = 0;
      } catch {
        /* ignore */
      }
      alertAudioRef.current = null;
    }
  }, []);

  const playKitchenAlert = useCallback(() => {
    const s = slaSettingsRef.current;
    if (s?.alertSoundEnabled === false) return;
    try {
      const soundUrl = resolveKitchenAlertSoundUrl(s?.alertSoundUrl);
      const volPct = s?.alertSoundVolume ?? 100;
      const volume = Math.min(1, Math.max(0, volPct / 100));
      stopKitchenAlertSound();
      const audio = new Audio(soundUrl);
      alertAudioRef.current = audio;
      audio.volume = volume;
      audio.onended = () => {
        if (alertAudioRef.current === audio) alertAudioRef.current = null;
      };
      // Catch promise rejections (e.g. AbortError when a subsequent stop pauses this play)
      // so they don't surface as "Uncaught (in promise)" in the console.
      audio.play().catch(() => { /* autoplay or interrupted */ });
    } catch {
      // Ignore autoplay errors; alert will play after first user interaction.
    }
  }, [stopKitchenAlertSound]);

  useEffect(() => {
    let cancelled = false;
    const refreshKitchenOrders = async () => {
      try {
        const todayStr = ymdInTimeZone(BUSINESS_TZ);
        const start = ymdToStartMs(todayStr);
        const end = ymdToEndMs(todayStr);
        const [todayRows, unpaid] = await Promise.all([
          ordersService.getByDateRange(start, end),
          ordersService.getOpenUnpaid(),
        ]);
        if (cancelled) return;
        const map = new Map<string, Order>();
        for (const o of unpaid) map.set(o.id, o);
        for (const o of todayRows) {
          if (isSampleOrDemoOrderId(String(o.id))) continue;
          if (o.hideFromKitchen) continue;
          if (o.orderType === 'buffet') {
            const extras = (o.items ?? []).some((item) => {
              if (item.removed || item.isBuffetPackage || item.isMealPlanSale) return false;
              const isDiscount = item.price < 0 || String(item.id).includes('combo-discount');
              return !isDiscount;
            });
            if (!extras) continue;
          }
          if (!isOpenKitchenTicketStatus(o)) continue;
          if (o.orderType === 'online' && o.paymentStatus !== 'paid') continue;
          if (!isKitchenRelevantCalendarDay(o)) continue;
          if (isStaleOnlineKitchenTicket(o)) continue;
          map.set(o.id, o);
        }
        setFetchedKitchenOrders(Array.from(map.values()));
      } catch (err) {
        console.error('Kitchen display: failed to load today orders', err);
      }
    };
    void refreshKitchenOrders();
    const timer = window.setInterval(() => void refreshKitchenOrders(), 300_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  /** Backfill kitchen snapshot for paid online orders created before auto-send on payment. */
  const patchedKitchenSnapshotRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const o of fetchedKitchenOrders) {
      if (o.orderType !== 'online' || o.paymentStatus !== 'paid') continue;
      if (!isKitchenRelevantCalendarDay(o) || isStaleOnlineKitchenTicket(o)) continue;
      if (o.initialKitchenSnapshot?.length) continue;
      if (!o.items?.length) continue;
      if (patchedKitchenSnapshotRef.current.has(o.id)) continue;
      patchedKitchenSnapshotRef.current.add(o.id);
      const snapshot = buildKitchenSnapshotFromOrder(o);
      if (snapshot.length === 0) continue;
      const existingLen = Math.max(
        o.initialKitchenSnapshot?.length ?? 0,
        o.lastSentItemSnapshot?.length ?? 0,
      );
      if (existingLen > 0 && snapshot.length < existingLen) continue;

      const nextInitial = pickLongerOrderSnapshot(o.initialKitchenSnapshot, snapshot) ?? snapshot;
      const nextLastSent = pickLongerOrderSnapshot(o.lastSentItemSnapshot, snapshot) ?? snapshot;
      void ordersService
        .update(o.id, { initialKitchenSnapshot: nextInitial, lastSentItemSnapshot: nextLastSent })
        .catch((err) => console.warn('Kitchen snapshot backfill failed', o.id, err));
    }
  }, [fetchedKitchenOrders]);

  // Deduplicate orders by ID — fetched fills gaps; live wins only when it still has items.
  // Incomplete live snapshots must not wipe tickets (that was causing orders to vanish on KDS).
  const uniqueOrders = useMemo(() => {
    const orderMap = new Map<string, Order>();
    fetchedKitchenOrders.forEach((order) => {
      orderMap.set(order.id, order);
    });
    orders.forEach((order) => {
      const existing = orderMap.get(order.id);
      orderMap.set(order.id, mergeOrderPreferComplete(existing, order));
    });
    return Array.from(orderMap.values()).filter(
      (o) => !o.isDeleted && orderHasMeaningfulItems(o),
    );
  }, [orders, fetchedKitchenOrders]);

  // Filter out orders not relevant to kitchen:
  // - Buffet orders (no kitchen prep)
  // - Orders explicitly hidden from kitchen
  // - Empty / discount-only orders
  const validOrders = useMemo(() => {
    return uniqueOrders.filter(o => {
      if (isSampleOrDemoOrderId(String(o.id))) return false;
      if (o.hideFromKitchen) return false;
      if (!isOpenKitchenTicketStatus(o)) return false;
      if (isStaleOnlineKitchenTicket(o)) return false;
      // Buffet: only show on KDS when there are add-on lines (drinks/desserts) to prepare
      if (o.orderType === 'buffet') {
        const extras = (o.items ?? []).filter((item) => {
          if (item.removed || item.isBuffetPackage || item.isMealPlanSale) return false;
          const isDiscount = item.price < 0 || String(item.id).includes('combo-discount');
          return !isDiscount;
        });
        if (extras.length === 0) return false;
      }
      // Filter out event/stall orders (handled on Mobile POS only)
      if (o.orderType === 'event') return false;
      // NZ business day (placed or pickup today) — aligned with POS Pending
      if (!isKitchenRelevantCalendarDay(o)) return false;

      // Online pickup: do not show in kitchen until payment has succeeded (order may exist while customer is on HPP).
      if (o.orderType === 'online' && o.paymentStatus === 'unpaid') return false;

      // Future online pickups: show in Scheduled until within lead time of pickup.
      if (isOnlineScheduledPickup(o)) return false;

      // Paid online orders: show on KDS with other new tickets (pickup time is shown on the card).
      // Filter out orders with no items
      if (!o.items || o.items.length === 0) return false;
      
      // Filter out orders that only have discount items (no actual items to prepare)
      const hasPreparableItems = o.items.some(item => {
        const isDiscount = item.price < 0 || item.id.toString().includes('combo-discount');
        return !isDiscount && !item.isMealPlanSale;
      });
      
      return hasPreparableItems;
    });
  }, [uniqueOrders]);

  const scheduledOnlineOrders = useMemo(() => {
    const now = Date.now();
    return uniqueOrders
      .filter((o) => {
        if (isSampleOrDemoOrderId(String(o.id))) return false;
        if (o.hideFromKitchen) return false;
        if (o.orderType !== 'online' || o.paymentStatus !== 'paid') return false;
        if (!isOpenKitchenTicketStatus(o)) return false;
        if (isStaleOnlineKitchenTicket(o, now)) return false;
        if (!isKitchenRelevantCalendarDay(o)) return false;
        if (!o.items?.length) return false;
        const pickupMs = normalizePickupTimeMs(o.pickupTime);
        if (pickupMs == null) return false;
        return pickupMs > now + ONLINE_KITCHEN_LEAD_MS;
      })
      .sort((a, b) => (normalizePickupTimeMs(a.pickupTime) ?? orderCreatedAtMs(a)) - (normalizePickupTimeMs(b.pickupTime) ?? orderCreatedAtMs(b)));
  }, [uniqueOrders]);
  
  // Per-card progression: each card (base or running order) moves independently
  // New: cards where no items are completed yet for that card
  // Preparing: cards where some but not all items are completed for that card
  // Awaiting Service: cards with all completed but not all served.
  const { newTickets, preparingTickets, awaitingServiceTickets } = useMemo(() => {
    const newT: KitchenTicketRow[] = [];
    const prepT: KitchenTicketRow[] = [];
    const awaitingT: KitchenTicketRow[] = [];

    validOrders.forEach((order) => {
      const snapshot = order.lastSentItemSnapshot ?? [];
      const items = order.items ?? [];
      const batches = order.orderBatches ?? [];
      const hasSnapshotAndDeltas = snapshot.length > 0 && (() => {
        const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));
        const currentNonRemoved = items.filter((i) => !i.removed);
        const hasNewOrDelta = currentNonRemoved.some((item, idx) => {
          const prev = item.lineId ? snapshotByLineId.get(item.lineId) : undefined;
          const prevByIndex = snapshot[idx];
          const prevQty = prev ?? prevByIndex;
          if (!prevQty) return true;
          return item.quantity > prevQty.quantity;
        });
        const hasRemoved =
          snapshot.length > currentNonRemoved.length ||
          snapshot.some((s) => !currentNonRemoved.some((i) => i.lineId === s.lineId));
        return hasNewOrDelta || hasRemoved;
      })();
      const hasMultipleBatches = batches.length > 1;

      const baseTicket = { order, batchIndex: undefined as number | undefined, batchId: undefined as string | undefined };
      const runningTickets =
        batches.length > 1
          ? batches.slice(1).map((batch, i) => ({ order, batchIndex: i + 1, batchId: batch.batchId }))
          : hasSnapshotAndDeltas
            ? [{ order, batchIndex: 1 as number, batchId: undefined as string | undefined }]
            : [];

      const ticketsForOrder = hasSnapshotAndDeltas || hasMultipleBatches ? [baseTicket, ...runningTickets] : [baseTicket];

      ticketsForOrder.forEach((ticket) => {
        const { items: cardItems, allCompleted, allServed } = getCardItemsWithTargets(order, ticket.batchIndex);
        if (cardItems.length === 0 || allServed) return;
        const cardBatchIndex = ticket.batchIndex != null ? ticket.batchIndex : 0;
        const anyCompleted = cardItems.some(({ item, targetQty }) =>
          getEffectiveQuantityCompletedForBatch(item, cardBatchIndex, targetQty) > 0
        );

        if (!anyCompleted && !allServed) {
          newT.push(ticket);
        } else if (anyCompleted && !allCompleted && !allServed) {
          prepT.push(ticket);
        } else if (allCompleted && !allServed) {
          // Awaiting Service: items are fully prepared but not all marked served (includes paid orders).
          // POS End-of-Day checklist counts these as "pending" until served/completed — they must appear here.
          awaitingT.push(ticket);
        }
      });
    });
    // Oldest orders first (by original order.timestamp) so the very first ticket is always at the top.
    const sortByPlacedTime = (a: { order: Order }, b: { order: Order }) =>
      (a.order.timestamp ?? 0) - (b.order.timestamp ?? 0);
    newT.sort(sortByPlacedTime);
    prepT.sort(sortByPlacedTime);
    awaitingT.sort(sortByPlacedTime);
    return { newTickets: newT, preparingTickets: prepT, awaitingServiceTickets: awaitingT };
  }, [validOrders]);

  const pendingStartAlertCount = useMemo(() => {
    return newTickets.filter(({ order, batchIndex }) => {
      if (typeof batchIndex === 'number' && batchIndex > 0) {
        return order.preparingStartedByBatch?.[String(batchIndex)] == null;
      }
      return order.status === 'new';
    }).length;
  }, [newTickets]);

  /** Single board: in-progress first, then not-started; each card shows listStage tag. */
  const activeBoardTickets = useMemo((): BoardTicketRow[] => {
    const sortByPlaced = (a: KitchenTicketRow, b: KitchenTicketRow) =>
      (a.order.timestamp ?? 0) - (b.order.timestamp ?? 0);
    const prep: BoardTicketRow[] = [...preparingTickets].sort(sortByPlaced).map((t) => ({ ...t, listStage: 'preparing' as const }));
    const fresh: BoardTicketRow[] = [...newTickets].sort(sortByPlaced).map((t) => ({ ...t, listStage: 'new' as const }));
    return [...prep, ...fresh];
  }, [newTickets, preparingTickets]);

  const gridHostRef = useRef<HTMLDivElement>(null);
  /** One full-height row of cards; extra tickets use pages (slim rail). */
  const [boardGrid, setBoardGrid] = useState({ cols: 4, rows: 1, perPage: 4 });

  const boardTotalPages = Math.max(1, Math.ceil(activeBoardTickets.length / boardGrid.perPage));
  const awaitingTotalPages = Math.max(1, Math.ceil(awaitingServiceTickets.length / boardGrid.perPage));
  const scheduledTotalPages = Math.max(1, Math.ceil(scheduledOnlineOrders.length / boardGrid.perPage));

  useEffect(() => {
    if (scheduledPage >= scheduledTotalPages) setScheduledPage(Math.max(0, scheduledTotalPages - 1));
  }, [scheduledPage, scheduledTotalPages]);

  useEffect(() => {
    if (boardPage >= boardTotalPages) setBoardPage(Math.max(0, boardTotalPages - 1));
  }, [boardPage, boardTotalPages]);

  useEffect(() => {
    if (awaitingPage >= awaitingTotalPages) setAwaitingPage(Math.max(0, awaitingTotalPages - 1));
  }, [awaitingPage, awaitingTotalPages]);

  const boardPageTickets = useMemo(
    () => activeBoardTickets.slice(boardPage * boardGrid.perPage, (boardPage + 1) * boardGrid.perPage),
    [activeBoardTickets, boardPage, boardGrid.perPage]
  );

  const awaitingPageTickets = useMemo(
    () => awaitingServiceTickets.slice(awaitingPage * boardGrid.perPage, (awaitingPage + 1) * boardGrid.perPage),
    [awaitingServiceTickets, awaitingPage, boardGrid.perPage]
  );

  const scheduledPageOrders = useMemo(
    () => scheduledOnlineOrders.slice(scheduledPage * boardGrid.perPage, (scheduledPage + 1) * boardGrid.perPage),
    [scheduledOnlineOrders, scheduledPage, boardGrid.perPage]
  );

  const cookListAggregates = useMemo(() => {
    const map = new Map<string, { name: string; remainingQty: number; orderIds: Set<string> }>();
    const accumulate = (tickets: KitchenTicketRow[]) => {
      tickets.forEach(({ order, batchIndex }) => {
        const { items: cardItems } = getCardItemsWithTargets(order, batchIndex);
        const cardBatchIndex = batchIndex != null ? batchIndex : 0;
        cardItems.forEach(({ item, targetQty }) => {
          if (item.removed) return;
          const done = getEffectiveQuantityCompletedForBatch(item, cardBatchIndex, targetQty);
          const remaining = Math.max(0, targetQty - done);
          if (remaining <= 0) return;
          const key = `${item.id}::${item.name.trim().toLowerCase()}`;
          const cur = map.get(key) ?? { name: item.name, remainingQty: 0, orderIds: new Set<string>() };
          cur.remainingQty += remaining;
          cur.orderIds.add(order.id);
          map.set(key, cur);
        });
      });
    };
    accumulate(newTickets);
    accumulate(preparingTickets);
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, name: v.name, remainingQty: v.remainingQty, orderCount: v.orderIds.size }))
      .filter((row) => row.remainingQty > 1)
      .sort((a, b) => b.remainingQty - a.remainingQty);
  }, [newTickets, preparingTickets]);

  useLayoutEffect(() => {
    const el = gridHostRef.current;
    if (!el) return;
    const update = () => {
      const fullW = el.clientWidth;
      const h = el.clientHeight;
      if (fullW < 120 || h < 120) return;
      const rows = 1;
      const minCell = 200;
      let cols = Math.floor(fullW / minCell);
      /* At most five tickets per row for wider cards / larger type; extras paginate (slim rail). */
      cols = Math.max(2, Math.min(5, cols));
      const perPage = rows * cols;
      setBoardGrid((prev) =>
        prev.cols === cols && prev.rows === rows && prev.perPage === perPage ? prev : { cols, rows, perPage }
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showAwaitingServiceView, showScheduledView, activeBoardTickets.length, awaitingServiceTickets.length, scheduledOnlineOrders.length, cookListAggregates.length]);

  const alertEnabled = slaSettings?.alertSoundEnabled !== false;

  useEffect(() => {
    if (pendingStartSkipInitialRef.current) {
      pendingStartSkipInitialRef.current = false;
      prevPendingStartRef.current = pendingStartAlertCount;
      return;
    }
    const prev = prevPendingStartRef.current ?? 0;
    if (pendingStartAlertCount > prev) {
      setNewColumnHeaderPulse(true);
      const id = window.setTimeout(() => setNewColumnHeaderPulse(false), 3500);
      prevPendingStartRef.current = pendingStartAlertCount;
      return () => window.clearTimeout(id);
    }
    prevPendingStartRef.current = pendingStartAlertCount;
  }, [pendingStartAlertCount]);

  useEffect(() => {
    if (!alertEnabled) {
      stopKitchenAlertSound();
      prevNewTicketCountRef.current = pendingStartAlertCount;
      return;
    }
    if (pendingStartAlertCount > prevNewTicketCountRef.current) {
      playKitchenAlert();
    }
    prevNewTicketCountRef.current = pendingStartAlertCount;
  }, [pendingStartAlertCount, playKitchenAlert, alertEnabled, stopKitchenAlertSound]);

  useEffect(() => {
    if (pendingStartAlertCount === 0) {
      stopKitchenAlertSound();
    }
  }, [pendingStartAlertCount, stopKitchenAlertSound]);

  useEffect(() => () => stopKitchenAlertSound(), [stopKitchenAlertSound]);

  useEffect(() => {
    if (!alertEnabled) return;
    if (pendingStartAlertCount === 0) return;
    const id = window.setInterval(() => {
      playKitchenAlert();
    }, 8000);
    return () => window.clearInterval(id);
  }, [pendingStartAlertCount, playKitchenAlert, alertEnabled]);

  // Show guard if register is closed (or while waiting on first snapshot).
  // Kitchen staff never open the register from KDS — that's a cashier action
  // on POS — so we hide the Open button here and surface a passive
  // "waiting on the cashier" message instead.
  if (!registerSession) {
    return (
      <RegisterClosedGuard
        onOpenRegister={onOpenRegister || (() => {})}
        currentUser={currentUser ?? undefined}
        isLoading={!registerSessionsHydrated}
        hideOpenButton
      />
    );
  }

  const handleMarkAllDone = () => {
    const ticketsToComplete = [...newTickets, ...preparingTickets];
    ticketsToComplete.forEach(({ order, batchIndex }) => {
      const { items: cardItems } = getCardItemsWithTargets(order, batchIndex);
      cardItems.forEach(({ originalIndex, targetQty }) => {
        onUpdateOrderItemStatus(order.id, originalIndex, 'completed', targetQty, batchIndex, targetQty);
      });
    });
  };

  if (phoneLayout) {
    if (showMealPlanBookingsView) {
      return (
        <div className="flex flex-col h-full min-h-0 bg-[#f6f3ee]">
          <KitchenMealPlanBookingsPanel onBack={() => setShowMealPlanBookingsView(false)} />
        </div>
      );
    }
    const phoneTickets = showScheduledView
      ? scheduledOnlineOrders.map((order) => ({ order, batchIndex: undefined as number | undefined, batchId: undefined as string | undefined, listStage: 'new' as const }))
      : showAwaitingServiceView
        ? awaitingServiceTickets.map((t) => ({ ...t, listStage: 'preparing' as const }))
        : activeBoardTickets;
    return (
      <div className="flex flex-col h-full min-h-0 bg-[#f6f3ee]">
        <div className="flex-shrink-0 flex gap-2 overflow-x-auto p-2">
          <button
            type="button"
            onClick={() => {
              setShowScheduledView(false);
              setShowAwaitingServiceView(false);
              setShowMealPlanBookingsView(false);
            }}
            className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${
              !showScheduledView && !showAwaitingServiceView && !showMealPlanBookingsView ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 border'
            }`}
          >
            Cook ({activeBoardTickets.length})
          </button>
          <button
            type="button"
            onClick={() => {
              setShowScheduledView(false);
              setShowAwaitingServiceView(false);
              setShowMealPlanBookingsView(true);
            }}
            className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${
              showMealPlanBookingsView ? 'bg-pink-600 text-white' : 'bg-white text-gray-700 border'
            }`}
          >
            Meal plans
          </button>
          <button
            type="button"
            onClick={() => {
              setShowScheduledView(false);
              setShowMealPlanBookingsView(false);
              setShowAwaitingServiceView(true);
            }}
            className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${
              showAwaitingServiceView ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border'
            }`}
          >
            Awaiting ({awaitingServiceTickets.length})
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAwaitingServiceView(false);
              setShowMealPlanBookingsView(false);
              setShowScheduledView(true);
            }}
            className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap ${
              showScheduledView ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'
            }`}
          >
            Scheduled ({scheduledOnlineOrders.length})
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-2 pb-8">
          {phoneTickets.length === 0 ? (
            <p className="text-sm text-center text-stone-600 py-10">No tickets in this list.</p>
          ) : (
            phoneTickets.map(({ order, batchIndex, batchId, listStage }, idx) => (
              <div
                key={batchIndex == null ? `${order.id}-phone-base` : `${order.id}-phone-run-${batchId ?? batchIndex}`}
                className="rounded-xl overflow-hidden shadow-sm min-h-[12rem]"
              >
                <KitchenOrderCard
                  order={order}
                  visibleBatchIndex={batchIndex}
                  onUpdateOrderItemStatus={onUpdateOrderItemStatus}
                  onStartOrder={onStartOrder}
                  onRemoveOrder={onRemoveOrder}
                  slaSettings={slaSettings}
                  compactMode
                  listStage={showAwaitingServiceView || showScheduledView ? undefined : listStage}
                  stickyMode={showAwaitingServiceView}
                  noteLayoutIndex={idx}
                />
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <main className="flex flex-1 flex-col min-h-0 overflow-hidden bg-[#f6f3ee] p-2 sm:p-3">
        {showMealPlanBookingsView ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg shadow-inner">
            <KitchenMealPlanBookingsPanel
              onBack={() => setShowMealPlanBookingsView(false)}
            />
          </div>
        ) : showScheduledView ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-[linear-gradient(160deg,#eef4ff_0%,#dbeafe_45%,#c7d9f5_100%)] shadow-inner">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-blue-300/70 bg-blue-50/90 px-3 py-2">
              <p className="text-xs font-semibold text-blue-900 sm:text-sm">
                {scheduledOnlineOrders.length} scheduled online pickup
                {scheduledOnlineOrders.length !== 1 ? 's' : ''}
                {scheduledTotalPages > 1 ? ` · Page ${scheduledPage + 1} of ${scheduledTotalPages}` : ''}
              </p>
              <button
                type="button"
                onClick={() => setShowScheduledView(false)}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-slate-700 text-white hover:bg-slate-800"
              >
                Back to Kitchen
              </button>
            </div>
            {scheduledOnlineOrders.length > 0 ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <KdsAwaitingPageBar
                  page={scheduledPage}
                  totalPages={scheduledTotalPages}
                  totalTickets={scheduledOnlineOrders.length}
                  perPage={boardGrid.perPage}
                  onChangePage={setScheduledPage}
                />
                <div className="flex min-h-0 flex-1 flex-row overflow-hidden">
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-2">
                    <div
                      className="grid h-full min-h-0 flex-1 gap-2"
                      style={{
                        gridTemplateColumns: `repeat(${boardGrid.cols}, minmax(0, 1fr))`,
                        gridTemplateRows: 'minmax(0, 1fr)',
                      }}
                    >
                      {scheduledPageOrders.map((order, idx) => (
                        <div key={`${order.id}-scheduled`} className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
                          <KitchenOrderCard
                            order={order}
                            onUpdateOrderItemStatus={onUpdateOrderItemStatus}
                            onStartOrder={onStartOrder}
                            onRemoveOrder={onRemoveOrder}
                            slaSettings={slaSettings}
                            compactMode
                            stickyMode
                            noteLayoutIndex={idx}
                            fitToCell
                            listStage="new"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <KdsStagePager
                    page={scheduledPage}
                    totalPages={scheduledTotalPages}
                    onChangePage={setScheduledPage}
                    aria-label="Scheduled pickup pages"
                    wideRail
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-8 text-blue-900/80">
                No scheduled online pickups right now.
              </div>
            )}
          </div>
        ) : showAwaitingServiceView ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-[linear-gradient(160deg,#f8f3ea_0%,#efe4d5_45%,#e4d7c8_100%)] shadow-inner">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-stone-300/70 bg-stone-100/80 px-3 py-2">
              <p className="text-xs font-semibold text-stone-700 sm:text-sm">
                {awaitingServiceTickets.length} order{awaitingServiceTickets.length !== 1 ? 's' : ''} awaiting service
                {awaitingTotalPages > 1 ? ` · Page ${awaitingPage + 1} of ${awaitingTotalPages}` : ''}
              </p>
              <button
                type="button"
                onClick={() => setShowAwaitingServiceView(false)}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-slate-700 text-white hover:bg-slate-800"
              >
                Back to Kitchen
              </button>
            </div>
            {awaitingServiceTickets.length > 0 ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <KdsAwaitingPageBar
                  page={awaitingPage}
                  totalPages={awaitingTotalPages}
                  totalTickets={awaitingServiceTickets.length}
                  perPage={boardGrid.perPage}
                  onChangePage={setAwaitingPage}
                />
                <div className="flex min-h-0 flex-1 flex-row overflow-hidden">
                <div ref={gridHostRef} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-2">
                  <div
                    className="grid h-full min-h-0 flex-1 gap-2"
                    style={{
                      gridTemplateColumns: `repeat(${boardGrid.cols}, minmax(0, 1fr))`,
                      gridTemplateRows: 'minmax(0, 1fr)',
                    }}
                  >
                    {awaitingPageTickets.map(({ order, batchIndex, batchId }, idx) => (
                      <div
                        key={batchIndex == null ? `${order.id}-await-base` : `${order.id}-await-run-${batchId ?? batchIndex}`}
                        className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
                      >
                        <KitchenOrderCard
                          order={order}
                          visibleBatchIndex={batchIndex}
                          onUpdateOrderItemStatus={onUpdateOrderItemStatus}
                          onStartOrder={onStartOrder}
                          onRemoveOrder={onRemoveOrder}
                          slaSettings={slaSettings}
                          compactMode
                          stickyMode
                          noteLayoutIndex={idx}
                          fitToCell
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <KdsStagePager
                  page={awaitingPage}
                  totalPages={awaitingTotalPages}
                  onChangePage={setAwaitingPage}
                  aria-label="Awaiting service pages"
                  wideRail
                />
                </div>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center p-8 text-stone-600">No orders awaiting service.</div>
            )}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
            <div
              className={`flex shrink-0 flex-col gap-2 rounded-xl border border-indigo-400/30 bg-[linear-gradient(145deg,#111b32_0%,#19284a_55%,#1f3564_100%)] px-3 py-2 text-white shadow-[0_8px_28px_rgba(10,18,35,0.35)] ring-1 ring-indigo-300/15 sm:flex-row sm:items-center sm:gap-3 ${
                newColumnHeaderPulse ? 'ring-2 ring-red-400/80' : ''
              }`}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <p className="shrink-0 text-[11px] font-black uppercase tracking-[0.14em] text-indigo-100 sm:text-xs">Cook list (qty 2+)</p>
                <div className="kds-hide-scrollbar flex min-w-0 flex-1 flex-nowrap gap-2 overflow-x-auto overflow-y-hidden py-0.5">
                  {cookListAggregates.length > 0 ? cookListAggregates.map((row) => (
                    <span
                      key={row.key}
                      className="inline-flex shrink-0 items-center rounded-lg border border-indigo-200/20 bg-white/12 px-2.5 py-1 text-xs font-semibold text-white shadow-sm ring-1 ring-indigo-200/15 backdrop-blur-[1px] sm:text-sm"
                    >
                      {row.remainingQty}× {row.name}
                      <span className="ml-1 text-[10px] font-semibold text-indigo-200/85 sm:text-xs">
                        ({row.orderCount})
                      </span>
                    </span>
                  )) : (
                    <span className="text-xs text-indigo-200/85 sm:text-sm">
                      {newTickets.length} new · {preparingTickets.length} preparing · {activeBoardTickets.length} on board
                    </span>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={() => {
                    setShowAwaitingServiceView(false);
                    setShowScheduledView(false);
                    setShowMealPlanBookingsView(true);
                  }}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-pink-600 text-white hover:bg-pink-700"
                >
                  Meal plans
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAwaitingServiceView(false);
                    setShowMealPlanBookingsView(false);
                    setShowScheduledView(true);
                  }}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  Scheduled ({scheduledOnlineOrders.length})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowScheduledView(false);
                    setShowMealPlanBookingsView(false);
                    setShowAwaitingServiceView(true);
                  }}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700"
                >
                  Awaiting Service ({awaitingServiceTickets.length})
                </button>
                {currentUser?.isAdmin && (
                  <button
                    type="button"
                    onClick={handleMarkAllDone}
                    className="px-3 py-1.5 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700"
                  >
                    Mark all done
                  </button>
                )}
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-[linear-gradient(160deg,#faf6ef_0%,#f1e8da_40%,#e8ddcd_100%)] shadow-[inset_0_2px_12px_rgba(0,0,0,0.05)]">
              {activeBoardTickets.length > 0 ? (
                <div className="flex min-h-0 flex-1 flex-row overflow-hidden">
                  <div ref={gridHostRef} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-2">
                    <div
                      className="grid h-full min-h-0 flex-1 gap-2"
                    style={{
                      gridTemplateColumns: `repeat(${boardGrid.cols}, minmax(0, 1fr))`,
                      gridTemplateRows: 'minmax(0, 1fr)',
                    }}
                    >
                      {boardPageTickets.map(({ order, batchIndex, batchId, listStage }, idx) => (
                        <div
                          key={batchIndex == null ? `${order.id}-base` : `${order.id}-run-${batchId ?? batchIndex}`}
                          className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
                        >
                          <KitchenOrderCard
                            order={order}
                            visibleBatchIndex={batchIndex}
                            onUpdateOrderItemStatus={onUpdateOrderItemStatus}
                            onStartOrder={onStartOrder}
                            onRemoveOrder={onRemoveOrder}
                            slaSettings={slaSettings}
                            compactMode
                            listStage={listStage}
                            noteLayoutIndex={idx}
                            fitToCell
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <KdsStagePager
                    page={boardPage}
                    totalPages={boardTotalPages}
                    onChangePage={setBoardPage}
                    aria-label="Kitchen board pages"
                  />
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center text-sm font-medium text-stone-700">
                  No active kitchen tickets.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default KitchenDisplayPage;